import { useContext, useLayoutEffect, useRef, useState, type ReactElement } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AuthContext } from "@bw/shared/hooks";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { FinanceApi } from "@bw/shared/api";
import type { FinanceAction, FinanceContextResponse } from "@bw/shared/types/generated";
import type { TeamPerson } from "@/libs/staffApi";

export interface PeopleFinanceAccessProps {
  member: Pick<TeamPerson, "org_id" | "team_member_id" | "personal_user_id" | "status">;
  context: FinanceContextResponse;
  api: FinanceApi;
  onChanged: () => void;
}

const choices: readonly [FinanceAction, string][] = [["org:finance_read", "Read financial reports"],
  ["org:finance_billing", "Manage company billing"], ["org:finance_disputes", "Manage disputes"],
  ["org:finance_repair", "Recover payout records"]];

export function PeopleFinanceAccess({ member, context, api, onChanged }: PeopleFinanceAccessProps): ReactElement {
  const actor = useContext(AuthContext)?.user?.uid ?? null;
  const cache = useQueryClient();
  const permitted = context.can_manage_finance_access && context.org_id === member.org_id;
  const activeMembership = member.status === "active";
  const billingReady = activeMembership && Boolean(member.personal_user_id);
  const key = ["org-finance-access", actor, context.org_id, context.work_session_revision,
    context.binding_generation, context.authority_generation, member.team_member_id, member.status] as const;
  const stamp = JSON.stringify(key) + permitted;
  const current = useRef(stamp); current.current = stamp;
  const [actions, setActions] = useState<FinanceAction[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const read = useQuery({ queryKey: key, queryFn: ({ signal }) => api.getTeamMemberFinanceAccess(
    member.org_id, member.team_member_id, signal), enabled: permitted, retry: false, staleTime: 0 });
  useLayoutEffect(() => { setSaving(false); }, [stamp]);
  useLayoutEffect(() => {
    setActions(activeMembership && read.data?.org_id === context.org_id
      ? read.data.saved_actions.filter(action => billingReady || action !== "org:finance_billing")
      : []);
    setError("");
  }, [stamp, read.data, context.org_id, activeMembership, billingReady]);
  const save = async (replacement: FinanceAction[]) => {
    if (!activeMembership) return;
    const started = current.current;
    setSaving(true); setError("");
    try {
      const result = await api.replaceTeamMemberFinanceAccess(member.org_id, member.team_member_id, { actions: replacement });
      if (current.current !== started || result.org_id !== member.org_id) return;
      cache.setQueryData(key, result); setActions([...result.saved_actions]); onChanged();
    } catch { if (current.current === started) setError("Finance access could not be changed. Refresh and try again."); }
    finally { if (current.current === started) setSaving(false); }
  };
  const updateAction = (action: FinanceAction, checked: boolean) => {
    if (!activeMembership) return;
    if (action === "org:finance_billing" && !billingReady) return;
    setActions(previous => {
      if (action === "org:finance_read" && !checked) return [];
      const selected = new Set(previous);
      if (checked) selected.add(action); else selected.delete(action);
      if (checked && action !== "org:finance_read") selected.add("org:finance_read");
      return choices.map(([item]) => item).filter(item => selected.has(item));
    });
  };
  if (!permitted) return <></>;
  if (read.isPending) return <CircularProgress aria-label="Loading finance access" size={20} />;
  if (read.isError || !read.data || read.data.org_id !== context.org_id) return <Alert severity="error">Finance access is unavailable.</Alert>;
  return <Stack spacing={1} component="section" aria-label="Finance access">
    <Typography component="h3" variant="subtitle2">Finance access</Typography>
    {error && <Alert severity="error">{error}</Alert>}
    {!activeMembership ? <Alert severity="info">Finance access is inactive because this person no longer has Team access.</Alert>
      : !billingReady ? <Alert severity="info">Company billing requires an active, accepted personal account. Other finance access can still be assigned.</Alert> : null}
    {choices.map(([action, label]) => <FormControlLabel key={action} label={label} control={<Checkbox size="small"
      checked={actions.includes(action)} disabled={!activeMembership || saving || read.isFetching || (action === "org:finance_billing" && !billingReady)}
      onChange={(_, checked) => updateAction(action, checked)} />} />)}
    <Typography variant="body2" color="text.secondary">Billing, disputes, and recovery include read access. Payment methods and subscriptions remain owned by the organization. Effective access: {activeMembership && read.data.effective_actions.length ?
      choices.filter(([action]) => read.data.effective_actions.includes(action)).map(([, label]) => label).join(", ") : "None"}.</Typography>
    <Stack direction="row" spacing={1}><Button size="small" variant="outlined" disabled={!activeMembership || saving || read.isFetching} onClick={() => void save(actions)}>Save</Button>
      <Button size="small" disabled={!activeMembership || saving || read.isFetching} onClick={() => void save([])}>Revoke finance access</Button></Stack>
  </Stack>;
}
